import { Injectable } from '@angular/core';
import {
  signInWithEmailAndPassword,
  signOut,
  Auth,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  User,
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  fetchSignInMethodsForEmail
} from '@angular/fire/auth';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { BehaviorSubject } from 'rxjs';

export interface AppUser {
  id: string;
  email: string;
  roles: string[];
  permissions: string[];
}
/*
    first_name: ['', Validators.required],
    last_name: ['', Validators.required],
    document_type_id: ['', Validators.required],
    document_number: ['', Validators.required],
    nationality: ['', Validators.required],
    birthdate: [null, Validators.required],
    residence_country: ['', Validators.required],
    phone_number: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]]
*/
export interface Persona {
  id: string;
  first_name: string;
  last_name: string;
  document_type_id: string;
  document_number: string;
  nationality: string;
  birthdate: string;
  residence_country: string;
  phone_number: string;
  email: string;
  is_default: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private backendUser: AppUser | null = null;
  private logueado: boolean = false;
  private personas: Persona[] = [];
  private userSubject = new BehaviorSubject<User | null>(null);

  constructor(private http: HttpClient, private auth: Auth) {
    onAuthStateChanged(this.auth, async (user) => {
      this.userSubject.next(user);
      
      // Sincronizar estado local con Firebase Auth
      if (user) {
        this.logueado = true;
        const backendUser = localStorage.getItem('backendUser');
        if (backendUser) {
          this.backendUser = JSON.parse(backendUser);
        } else {
          this.backendUser = await this.getBackendUser(user);
          localStorage.setItem('backendUser', JSON.stringify(this.backendUser));
        }
      } else {
        this.logueado = false;
        this.backendUser = null;
      }
    });
  }
  
  get user$() {
    return this.userSubject.asObservable();
  }

  async getBackendUser(user: User) {
    const res = await this.http.get(`${environment.backendApiUrl}/users/me/${user.uid}`, {
      headers: {
        Authorization: `Bearer ${user.getIdToken()}`
      }
    }).toPromise();
    return res as AppUser;
  }

  async login(email: string, password: string, recordar: boolean) {
    return signInWithEmailAndPassword(this.auth, email, password).then(async (result) => {
      if (result.user) {
        this.logueado = true;
        this.backendUser = await this.getBackendUser(result.user);
        localStorage.setItem('backendUser', JSON.stringify(this.backendUser));
        this.userSubject.next(result.user);
      }
      return result;
    });
  }

  enviarRecuperacion(email: string) {
    return sendPasswordResetEmail(this.auth, email);
  }

  register(email: string, password: string) {
    return createUserWithEmailAndPassword(this.auth, email, password);
  }

  get user(): AppUser | null {
    return this.backendUser;
  }

  logout() {
    this.backendUser = null;
    this.logueado = false;
    return signOut(this.auth);
  }

  getCurrentUser(): AppUser | null {
    return this.backendUser;
  }

  hasPermission(permission: string): boolean {
    return this.backendUser?.permissions.includes(permission) ?? false;
  }

  isAuthenticated(): boolean {
    return this.logueado;
  }

  getPersonas(): Persona[] {
    return this.logueado ? this.personas : [];
  }

  buscarPersonaPorDni(dni: string): Persona | null {
    return this.personas.find(p => p.document_number === dni) || null;
  }
  
  registrarPersona(data: Partial<Persona>): Persona {
    const nueva: Persona = {
      id: (this.personas.length + 1).toString(),
      first_name: data.first_name || '',
      last_name: data.last_name || '',
      document_type_id: data.document_type_id || '',
      document_number: data.document_number || '',
      nationality: data.nationality || '',
      birthdate: data.birthdate || '',
      residence_country: data.residence_country || '',
      phone_number: data.phone_number || '',
      email: data.email || '',
      is_default: false
    };
    this.personas.push(nueva);
    return nueva;
  }

  loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account'
    });
    return signInWithPopup(this.auth, provider).then(result => {
      const user = result.user;
      if (user) {
        this.sincronizarUsuario(user, {});
        this.logueado = true;
        this.backendUser = {
          id: user.uid,
          email: user.email ?? '',
          roles: ['client'],
          permissions: []
        };
      }
      return result;
    });
  }

  loginWithFacebook() {
    const provider = new FacebookAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account'
    });
    return signInWithPopup(this.auth, provider).then(result => {
      const user = result.user;
      if (user) {
        this.sincronizarUsuario(user, {});
        this.logueado = true;
        this.backendUser = {
          id: user.uid,
          email: user.email ?? '',
          roles: ['client'],
          permissions: []
        };
      }
      return result;
    });
  }

  getSignInMethods(email: string) {
    return fetchSignInMethodsForEmail(this.auth, email).then(methods => {
      console.log('methods: ', methods);
      return methods;
    });
  }

  async sincronizarUsuario(user: User, profileData: any) {
    const token = await user.getIdToken();
  
    const body = {
      uid: user.uid,
      email: user.email,
      name: user.displayName ?? '',
      photo: user.photoURL ?? '',
      provider: user.providerData?.[0]?.providerId ?? 'password',
      ...profileData
    };
  
    return this.http.post(`${environment.backendApiUrl}/users/sync`, body, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).toPromise();
  }

  isAdmin(): boolean {
    return this.backendUser?.roles.includes('admin') ?? false;
  }
}